/**
 * BucketDrilldown — modal opened when you click a bucket card. Shows:
 *   - Learned style brief (from content-brand-learn)
 *   - Top performing posts (this bucket)
 *   - Recent drafts (this bucket)
 *   - Sub-category breakdown of references
 *   - Best-performing references
 */
import { X, Sparkles, TrendingUp, FileText, Folder } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/charts/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useBucketDrilldown } from '@/hooks/useBucketDrilldown';
import { driveImgSrc } from '@/lib/driveImg';

export function BucketDrilldown({ slug, onClose }: { slug: string | null; onClose: () => void }) {
  const q = useBucketDrilldown(slug);
  if (!slug) return null;
  const data = q.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-lg border border-border bg-background p-4 shadow-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {q.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : !data?.bucket ? (
          <EmptyState title="Bucket not found" description="" />
        ) : (
          <div className="space-y-4 pr-6">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ background: data.bucket.color_accent ?? '#888' }} />
              <div>
                <div className="text-lg font-semibold">{data.bucket.name}</div>
                <div className="text-2xs text-muted-foreground">{data.bucket.description}</div>
              </div>
              {data.bucket.target_monthly && (
                <span className="ml-auto rounded bg-muted/50 px-2 py-0.5 text-2xs text-muted-foreground">
                  target · {data.bucket.target_monthly}/mo
                </span>
              )}
            </div>

            {/* Learned style */}
            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Learned style
                  {data.bucket.learned_updated_at && (
                    <span className="ml-auto text-2xs text-muted-foreground">
                      updated {new Date(data.bucket.learned_updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
                {data.bucket.learned_style_notes ? (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                    {data.bucket.learned_style_notes}
                  </p>
                ) : (
                  <p className="text-2xs text-muted-foreground">
                    Not learned yet — the weekly Sunday 4 AM UTC cycle needs at least one published post to distill.
                  </p>
                )}
                {data.bucket.brand_voice_notes && (
                  <div className="pt-2 border-t border-border/40 text-2xs text-muted-foreground">
                    <span className="font-medium text-foreground">Default voice:</span> {data.bucket.brand_voice_notes}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top performing posts */}
            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500" /> Top posts (Studio-authored)
                </div>
                {data.topPosts.length === 0 ? (
                  <p className="text-2xs text-muted-foreground">No published Studio posts yet.</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-6">
                    {data.topPosts.map((p) => (
                      <a key={p.media_id} href={p.permalink ?? '#'} target="_blank" rel="noreferrer" className="group block">
                        <div className="relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted/30">
                          {p.thumbnail_url && (
                            <img src={p.thumbnail_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                          )}
                          <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-2xs text-white tabular-nums">
                            <span>♥{p.like_count}</span>
                            {p.reach != null && <span>reach {compactN(p.reach)}</span>}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent drafts */}
            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <FileText className="h-3.5 w-3.5 text-violet-500" /> Recent drafts ({data.drafts.length})
                </div>
                {data.drafts.length === 0 ? (
                  <p className="text-2xs text-muted-foreground">No drafts in this bucket.</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {data.drafts.map((d) => (
                      <div key={d.id} className="relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted/20">
                        {d.thumb_key ? (
                          <img src={`/api/media/${d.thumb_key}`} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-2xs text-muted-foreground/60">no image</div>
                        )}
                        <div className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-2xs uppercase text-white backdrop-blur">{d.status}</div>
                        <div className="absolute bottom-0 left-0 right-0 truncate bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1 text-2xs text-white/80">
                          {d.caption?.slice(0, 30) ?? d.post_kind}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sub-category breakdown */}
            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <Folder className="h-3.5 w-3.5 text-amber-500" /> Sub-categories · references
                </div>
                {data.subCategories.length === 0 ? (
                  <p className="text-2xs text-muted-foreground">No references yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {data.subCategories.map((s) => (
                      <span key={s.name} className="rounded-full bg-muted/40 px-2 py-1 text-2xs">
                        {s.name} <span className="ml-0.5 text-muted-foreground tabular-nums">· {s.count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Best references */}
            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500" /> References sorted by past performance
                </div>
                {data.refs.length === 0 ? (
                  <p className="text-2xs text-muted-foreground">No references synced.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 md:grid-cols-8">
                    {data.refs.slice(0, 24).map((r) => {
                      const perf = r.avg_engagement_pct;
                      return (
                        <div
                          key={r.id}
                          className={cn(
                            'relative aspect-square overflow-hidden rounded-md border',
                            perf != null && perf >= 2 ? 'border-emerald-500/60 ring-1 ring-emerald-500/30' : 'border-border/40',
                          )}
                          title={`${r.original_name} · ${r.posts_used_in ?? 0}× · ${perf != null ? perf.toFixed(1) + '%' : '—'}`}
                        >
                          {(r.drive_file_id || r.drive_thumbnail_link) && (
                            <img src={driveImgSrc(r.drive_file_id, r.drive_thumbnail_link, 400) ?? ''} referrerPolicy="no-referrer" alt="" className="h-full w-full object-cover" />
                          )}
                          {perf != null && r.posts_used_in && r.posts_used_in > 0 && (
                            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 px-1 py-0.5 text-2xs text-white tabular-nums backdrop-blur">
                              <span>{r.posts_used_in}×</span>
                              <span>{perf.toFixed(1)}%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function compactN(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}
